import { applySortStrategy, getPossibleOrientations, SORT_STRATEGIES, SpatialGrid } from './common.js';

/**
 * Skyline Bottom-Left Fill algorithm
 * Maintains a skyline profile and places pieces where they cause minimum waste.
 */

function createSheet(id, sheetWidth, sheetHeight) {
  return {
    id,
    width: sheetWidth,
    height: sheetHeight,
    pieces: [],
    grid: new SpatialGrid(sheetWidth, sheetHeight),
  };
}

/**
 * Check if piece at (px, py) with (pw, ph) overlaps any existing placed piece,
 * respecting cutWidth as minimum gap. Uses spatial grid for O(1) amortized checks.
 */
function hasOverlap(sheet, px, py, pw, ph, cutWidth) {
  return sheet.grid.hasCollision(px, py, pw, ph, cutWidth);
}

/**
 * Find best position using simple bottom-left scan with skyline heuristic.
 * We try candidate positions derived from existing placements.
 */
function findPosition(sheet, pw, ph, sheetWidth, sheetHeight, cutWidth) {
  const candidates = [{ x: 0, y: 0 }];
  for (const r of sheet.pieces) {
    candidates.push({ x: r.x + r.width + cutWidth, y: r.y });
    candidates.push({ x: r.x, y: r.y + r.height + cutWidth });
    candidates.push({ x: r.x + r.width + cutWidth, y: 0 });
    candidates.push({ x: 0, y: r.y + r.height + cutWidth });
  }

  candidates.sort((a, b) => a.y - b.y || a.x - b.x);

  for (const pos of candidates) {
    if (pos.x < 0 || pos.y < 0) continue;
    if (pos.x + pw > sheetWidth || pos.y + ph > sheetHeight) continue;
    if (!hasOverlap(sheet, pos.x, pos.y, pw, ph, cutWidth)) {
      return { x: pos.x, y: pos.y };
    }
  }
  return null;
}

/**
 * Skyline optimization algorithm
 */
export const skylineOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy) => {
  const sorted = sortStrategy ? applySortStrategy(allPieces, sortStrategy) : applySortStrategy(allPieces, SORT_STRATEGIES.DESCSS);
  const sheets = [createSheet(1, sheetWidth, sheetHeight)];
  const unplacedPieces = [];

  for (const piece of sorted) {
    const orientations = getPossibleOrientations(piece);
    let placed = false;

    // Try each orientation on each existing sheet
    for (const orient of orientations) {
      if (placed) break;
      for (const sheet of sheets) {
        const pos = findPosition(sheet, orient.width, orient.height, sheetWidth, sheetHeight, cutWidth);
        if (pos) {
          const placedPiece = { ...orient, x: pos.x, y: pos.y };
          sheet.pieces.push(placedPiece);
          sheet.grid.insert(placedPiece);
          placed = true;
          break;
        }
      }
    }

    if (!placed) {
      // Try new sheet
      const newSheet = createSheet(sheets.length + 1, sheetWidth, sheetHeight);
      for (const orient of orientations) {
        const pos = findPosition(newSheet, orient.width, orient.height, sheetWidth, sheetHeight, cutWidth);
        if (pos) {
          const placedPiece = { ...orient, x: pos.x, y: pos.y };
          newSheet.pieces.push(placedPiece);
          newSheet.grid.insert(placedPiece);
          sheets.push(newSheet);
          placed = true;
          break;
        }
      }
      if (!placed) {
        unplacedPieces.push(piece);
      }
    }
  }

  const layouts = sheets.filter(s => s.pieces.length > 0).map((s, idx) => ({
    id: idx + 1, width: s.width, height: s.height, pieces: s.pieces,
  }));

  return { layouts, unplacedPieces, unplacedCount: unplacedPieces.length };
};
